import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  MenuItem,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { Context } from '../../service/Context';
import theme from '../../styles/theme';
import { CATEGORIAS_ORDEN, formatClubTeamName, ordenarPorCategoria } from '../../utils/UtilesComun';
import {
  jugadorAptoParaCategoria,
  obtenerCategoriaCodigo,
  obtenerEstadoJugadorEnCategoria,
} from '../../utils/UtilesJugador';
import BarraBuscarJugador from '../buscadores/BarraBuscarJugador';
import {
  BoxTitleApp,
  ButtonApp,
  DialogApp,
  PaperApp,
  SelectApp,
  SelectWrapperApp,
  TypographyApp,
} from '../custom/CustomComponents';
import { CampogramPlan, SlotRolPlan } from '../custom/DedicatedComponents';
import FichaJugadorPlan, { BadgeCategoria } from './FichaJugadorPlan';
import { FORMACIONES } from './FormacionesDatos';

/**
 * Convierte un año de inicio en una cadena de temporada (ej. 2024 -> '2024-2025')
 */
const formatTemporada = (ano) => `${ano}-${ano + 1}`;

const capitalizar = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '');

const formatearNombre = (nombreCompleto) => {
  if (!nombreCompleto) {
    return '';
  }
  if (nombreCompleto.includes(',')) {
    const [apellidos, nombre] = nombreCompleto.split(',').map((s) => s.trim());
    const primerApellido = apellidos.split(' ')[0];
    return `${capitalizar(nombre)} ${capitalizar(primerApellido)}`;
  }
  const partes = nombreCompleto.trim().split(' ');
  if (partes.length <= 2) {
    return nombreCompleto;
  }
  return `${partes[0]} ${partes[1]}`;
};

// Mapeo posicion de campo → grupo del sidebar
const POSICION_A_GRUPO = {
  // Fútbol 11
  Portero: 'Porteros',
  'Lateral izquierdo': 'Defensas',
  'Lateral derecho': 'Defensas',
  'Central izquierdo': 'Defensas',
  'Central derecho': 'Defensas',
  'Central centro': 'Defensas',
  'Carrilero izquierdo': 'Defensas',
  'Carrilero derecho': 'Defensas',
  Pivote: 'Centrocampistas',
  'Interior izquierdo': 'Centrocampistas',
  'Interior derecho': 'Centrocampistas',
  'Volante izquierdo': 'Centrocampistas',
  'Volante derecho': 'Centrocampistas',
  Mediapunta: 'Centrocampistas',
  'Extremo izquierdo': 'Delanteros',
  'Extremo derecho': 'Delanteros',
  'Delantero centro': 'Delanteros',
  'Delantero izquierdo': 'Delanteros',
  'Delantero derecho': 'Delanteros',
  // Fútbol 7
  'Defensa izquierdo': 'Defensas',
  'Defensa central': 'Defensas',
  'Defensa derecho': 'Defensas',
  'Mediocampista izquierdo': 'Centrocampistas',
  'Mediocampista central': 'Centrocampistas',
  'Mediocampista derecho': 'Centrocampistas',
  Delantero: 'Delanteros',
};

const ROLES = ['Titular', 'Suplente', 'Alternativa'];
// Determina la modalidad a partir de una clave de formación guardada
const determinarModalidad = (formacion) => {
  if (FORMACIONES.F7[formacion]) {
    return 'F7';
  }
  return 'F11';
};

const PlanificadorPlantilla = ({ plantilla, idEquipo, datosEquipo, equiposClub }) => {
  const {
    temporadaActual,
    obtenerPlanificacionEquipo,
    guardarPlanificacionEquipo,
    obtenerPlantilla,
    obtenerHistoricoEquipo,
    obtenerDatosJugador,
  } = useContext(Context);

  const [modalidad, setModalidad] = useState('F11');
  const [formacionSeleccionada, setFormacionSeleccionada] = useState('4-3-3');
  const [jugadoresPlan, setJugadoresPlan] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const isInitialMount = useRef(true);
  const timeoutRef = useRef(null);

  const [plantillaInferior, setPlantillaInferior] = useState([]);
  const [openDialogAnadir, setOpenDialogAnadir] = useState(false);
  const [equipoModalId, setEquipoModalId] = useState('');

  // Estados nuevos para filtrado y fichajes
  const anoInicial = temporadaActual ? parseInt(temporadaActual.split('-')[0], 10) : new Date().getFullYear();
  const [temporadaPlan, setTemporadaPlan] = useState(anoInicial + 1); // Por defecto proxima temporada
  const [jugadoresExtra, setJugadoresExtra] = useState([]); // Fichajes añadidos manualmente

  const opcionesTemporada = [
    { value: anoInicial, label: `${anoInicial}-${anoInicial + 1} (Actual)` },
    { value: anoInicial + 1, label: `${anoInicial + 1}-${anoInicial + 2} (Próxima)` },
    { value: anoInicial + 2, label: `${anoInicial + 2}-${anoInicial + 3} (En 2 años)` },
  ];

  const procesarRespuesta = (planificacionGeneral, plantillaBase) => {
    const formacionDb = planificacionGeneral?.formacionPlanificacion || '4-3-3';
    const jugadoresDb = planificacionGeneral?.jugadores || [];

    const modalidadDb = determinarModalidad(formacionDb);
    const formacionValida = FORMACIONES[modalidadDb][formacionDb]
      ? formacionDb
      : Object.keys(FORMACIONES[modalidadDb])[0];

    setModalidad(modalidadDb);
    setFormacionSeleccionada(formacionValida);

    // Mapeamos los jugadores de la plantilla base
    const baseUnida = [...plantillaBase];

    // Ahora agregamos también aquellos jugadoresDb que no pertenezcan a plantillaBase
    // extraLista no se usa directamente aquí porque al recargar, la info vendrá de la bbdd (jugadoresDb)
    jugadoresDb.forEach((dbJugador) => {
      if (!baseUnida.some((j) => j.idJugador === dbJugador.idJugador)) {
        baseUnida.push({
          idJugador: dbJugador.idJugador,
          nombreJugador: dbJugador.nombreJugador,
          demarcacion: dbJugador.demarcacion,
          anoNacimiento: dbJugador.anoNacimiento,
          esFichaje: true,
          fuente: dbJugador.fuente || 'externo',
          estado: dbJugador.estado || 'Posible',
        });
      }
    });

    return baseUnida.map((j) => {
      const enPlanDB = jugadoresDb.find((p) => p.idJugador === j.idJugador);
      if (enPlanDB) {
        return {
          ...j,
          posicion: enPlanDB.posicion,
          rol: enPlanDB.rol,
          grupoSidebar: POSICION_A_GRUPO[enPlanDB.posicion] || 'Otros',
          esFichaje: j.esFichaje || !!enPlanDB.estado,
          fuente: j.fuente || 'interno',
          estado: enPlanDB.estado || j.estado || 'Confirmado',
        };
      }
      return {
        ...j,
        posicion: null,
        rol: 'Espera',
        grupoSidebar: 'Otros',
        esFichaje: j.esFichaje || false,
        fuente: j.fuente || 'interno',
        estado: j.estado || 'Confirmado',
      };
    });
  };

  useEffect(() => {
    const cargarDatos = async () => {
      setCargando(true);
      const planificacionGeneral = await obtenerPlanificacionEquipo(idEquipo, formatTemporada(temporadaPlan));
      setJugadoresPlan(procesarRespuesta(planificacionGeneral, plantilla, jugadoresExtra));
      setIsDirty(false); // Reiniciamos el indicador de dirty tras cargar
      setCargando(false);
    };

    if (plantilla && idEquipo) {
      cargarDatos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantilla, idEquipo, temporadaPlan]);

  // Auto-guardado con Debounce
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!isDirty) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      const dtoJugadores = jugadoresPlan
        .filter((j) => j.posicion !== null || j.esFichaje || j.estado !== 'Confirmado' || j.rol === 'Omitido')
        .map((j) => ({
          idEquipo,
          idJugador: j.idJugador,
          nombreJugador: j.nombreJugador,
          demarcacion: j.demarcacion,
          posicion: j.posicion,
          rol: j.rol,
          estado: j.estado === 'En Plantilla' ? null : j.estado,
          fuente: j.fuente,
        }));

      const exito = await guardarPlanificacionEquipo(idEquipo, {
        temporada: formatTemporada(temporadaPlan),
        formacionPlanificacion: formacionSeleccionada,
        jugadores: dtoJugadores,
      });

      if (exito) {
        setIsDirty(false);
      }
    }, 1500); // 1.5s de debounce

    return () => clearTimeout(timeoutRef.current);
  }, [jugadoresPlan, formacionSeleccionada, isDirty, idEquipo, temporadaPlan, guardarPlanificacionEquipo]);

  // Cargar plantilla del equipo inferior / seleccionado únicamente cuando el diálogo esté abierto
  useEffect(() => {
    const fetchPlantillaInferior = async () => {
      if (!openDialogAnadir || !datosEquipo || !equiposClub || equiposClub.length === 0) {
        return;
      }

      let idEquipoFetch = equipoModalId;

      if (!idEquipoFetch) {
        const normCat = (datosEquipo.categoria || '').toUpperCase();
        const indiceActual = CATEGORIAS_ORDEN.indexOf(normCat);

        if (indiceActual !== -1 && indiceActual < CATEGORIAS_ORDEN.length - 1) {
          const catInferior = CATEGORIAS_ORDEN[indiceActual + 1];
          const equiposInferiores = equiposClub.filter((eq) => (eq.categoria || '').toUpperCase() === catInferior);

          if (equiposInferiores.length > 0) {
            idEquipoFetch = equiposInferiores[0].idEquipo;
            setEquipoModalId(idEquipoFetch);
          }
        }
      }

      if (idEquipoFetch) {
        const historicoInf = await obtenerHistoricoEquipo(idEquipoFetch);
        const idLigaInf = historicoInf && historicoInf.length > 0 ? historicoInf[0].idLiga : undefined;

        const plantillaInf = await obtenerPlantilla(idEquipoFetch, idLigaInf);
        setPlantillaInferior(plantillaInf || []);
      } else {
        setPlantillaInferior([]);
      }
    };
    fetchPlantillaInferior();
  }, [openDialogAnadir, datosEquipo, equiposClub, obtenerPlantilla, obtenerHistoricoEquipo, equipoModalId]);

  // Libera los jugadores en posiciones que no existen en la nueva formación
  const handleFormacionChange = (nuevaFormacion) => {
    const nuevaMod = determinarModalidad(nuevaFormacion);

    // Si cambia de modalidad (F11 <-> F7), limpiamos el campo por completo
    if (nuevaMod !== modalidad) {
      setJugadoresPlan((prev) => prev.map((j) => ({ ...j, posicion: null, rol: 'Espera' })));
      setModalidad(nuevaMod);
    } else {
      // Si solo cambia la formación dentro de la misma modalidad, liberamos los que no encajen
      const posicionesValidas = new Set(FORMACIONES[nuevaMod][nuevaFormacion].posiciones.map((p) => p.id));
      setJugadoresPlan((prev) =>
        prev.map((j) => {
          if (j.posicion && !posicionesValidas.has(j.posicion)) {
            return { ...j, posicion: null, rol: 'Espera' };
          }
          return j;
        })
      );
    }
    setFormacionSeleccionada(nuevaFormacion);
    setIsDirty(true);
  };

  const handleDragStart = (e, jugador) => {
    e.dataTransfer.setData('jugadorId', jugador.idJugador.toString());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropSidebar = (e, grupoDestino) => {
    e.preventDefault();
    const jugadorId = parseInt(e.dataTransfer.getData('jugadorId'), 10);
    if (!jugadorId) {
      return;
    }
    setJugadoresPlan((prev) =>
      prev.map((j) =>
        j.idJugador === jugadorId ? { ...j, posicion: null, rol: 'Espera', grupoSidebar: grupoDestino } : j
      )
    );
    setIsDirty(true);
  };

  const handleDropCampo = (e, posicionDestino, rolDestino) => {
    e.preventDefault();
    e.stopPropagation();
    const jugadorId = parseInt(e.dataTransfer.getData('jugadorId'), 10);
    if (!jugadorId) {
      return;
    }

    const grupoAutomatico = POSICION_A_GRUPO[posicionDestino] || 'Otros';

    const ocupantePrevio = jugadoresPlan.find(
      (j) => j.posicion === posicionDestino && j.rol === rolDestino && j.idJugador !== jugadorId
    );

    setJugadoresPlan((prev) =>
      prev.map((j) => {
        if (j.idJugador === jugadorId) {
          return { ...j, posicion: posicionDestino, rol: rolDestino, grupoSidebar: grupoAutomatico };
        }
        if (ocupantePrevio && j.idJugador === ocupantePrevio.idJugador) {
          return { ...j, posicion: null, rol: 'Espera' };
        }
        return j;
      })
    );
    setIsDirty(true);
  };

  const handleExtraJugador = async (jugador, fuente = 'externo') => {
    // jugador viene del Context y tiene { idJugador, nombreJugador, ano } o similar
    const idJugador = parseInt(jugador.idJugador || jugador.first, 10);
    if (!jugadoresPlan.some((j) => j.idJugador === idJugador)) {
      let anoNacimientoFetch = jugador.ano || jugador.third;
      if (!anoNacimientoFetch && fuente === 'externo') {
        try {
          const datosCompletos = await obtenerDatosJugador(idJugador);
          if (datosCompletos && datosCompletos.ano) {
            anoNacimientoFetch = datosCompletos.ano;
          }
        } catch (e) {
          console.error('Error fetching external player info', e);
        }
      }

      setJugadoresPlan((prev) => [
        ...prev,
        {
          idJugador,
          nombreJugador: jugador.nombreJugador || jugador.nombre || jugador.second || 'Desconocido',
          demarcacion: jugador.demarcacion || 'Desconocida',
          anoNacimiento: anoNacimientoFetch || null,
          esFichaje: true,
          fuente: fuente,
          estado: 'Confirmado',
          posicion: null,
          rol: 'Espera',
          grupoSidebar: 'Otros',
        },
      ]);
      setJugadoresExtra((prev) => [...prev, { idJugador }]);
      setIsDirty(true);
    }
  };

  const handleActionClick = (jugadorItem, enCampo) => {
    if (enCampo) {
      // 1. Está en el campo: Lo devolvemos al sidebar
      setJugadoresPlan((prev) =>
        prev.map((j) =>
          j.idJugador === jugadorItem.idJugador
            ? {
                ...j,
                posicion: null,
                rol: 'Espera',
                grupoSidebar: 'Otros',
              }
            : j
        )
      );
      setIsDirty(true);
    } else {
      // 2. Está en el sidebar
      if (jugadorItem.esFichaje) {
        setJugadoresPlan((prev) => prev.filter((j) => j.idJugador !== jugadorItem.idJugador));
        setJugadoresExtra((prev) => prev.filter((je) => je.idJugador !== jugadorItem.idJugador));
        setIsDirty(true);
      } else {
        // En lugar de eliminar, lo marcamos como Omitido para que se guarde en BBDD
        setJugadoresPlan((prev) =>
          prev.map((j) => (j.idJugador === jugadorItem.idJugador ? { ...j, rol: 'Omitido', posicion: null } : j))
        );
        setIsDirty(true);
      }
    }
  };

  const handleEstadoChange = (idJugador, nuevoEstado) => {
    setJugadoresPlan((prev) =>
      prev.map((j) => (j.idJugador === idJugador ? { ...j, estado: nuevoEstado, esFichaje: true } : j))
    );
    setIsDirty(true);
  };

  const renderFicha = (jugador, enCampo = false) => {
    const asignado = !enCampo && jugador.rol !== 'Espera';
    const anoNac = jugador.anoNacimiento || jugador.ano;
    const codigoCat = anoNac ? obtenerCategoriaCodigo(temporadaPlan + 1, anoNac) : '';
    const estadoActual = jugador.estado || (jugador.esFichaje ? 'Posible' : 'Confirmado');

    const isOutdated =
      anoNac && datosEquipo?.categoria
        ? !jugadorAptoParaCategoria(temporadaPlan + 1, anoNac, datosEquipo.categoria)
        : false;

    let posicionAbreviada = '';
    if (jugador.posicion && formacionActual && formacionActual.posiciones) {
      const posConfig = formacionActual.posiciones.find((p) => p.id === jugador.posicion);
      if (posConfig) {
        posicionAbreviada = posConfig.label;
      }
    }

    return (
      <FichaJugadorPlan
        key={jugador.idJugador}
        jugador={{ ...jugador, nombreJugador: formatearNombre(jugador.nombreJugador) }}
        rol={jugador.rol ? jugador.rol.toLowerCase() : 'espera'}
        enCampo={enCampo}
        asignado={asignado}
        esFichaje={jugador.esFichaje}
        draggable
        onDragStart={(e) => handleDragStart(e, jugador)}
        codigoCat={codigoCat}
        estado={estadoActual}
        onEstadoChange={handleEstadoChange}
        onActionClick={handleActionClick}
        isOutdated={isOutdated}
        fuente={jugador.fuente || 'interno'}
        categoriaEquipo={datosEquipo?.categoria}
        posicionAbreviada={posicionAbreviada}
      />
    );
  };

  if (cargando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const formacionActual = FORMACIONES[modalidad][formacionSeleccionada];

  return (
    <PaperApp sx={{ mt: 3 }}>
      {/* Header con selectores de configuración */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
          alignItems: 'center',
          px: 3,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Lado izquierdo: Título */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              fontSize: theme.typography.h2.fontSize,
              color: theme.palette.text.primary,
              fontWeight: 500,
            }}
          >
            Planificación
          </Typography>
        </Box>

        {/* Centro: Selector de Temporada */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <SelectApp
            value={temporadaPlan}
            onChange={(e) => setTemporadaPlan(parseInt(e.target.value, 10))}
            variant="standard"
            disableUnderline
            sx={{
              backgroundColor: 'transparent',
              minWidth: 250,
              fontSize: '0.9rem',
              borderColor: theme.palette.primary.main,
              color: theme.palette.text.secondary,
              fontWeight: 400,
              textAlign: 'center',
              '& .MuiSelect-select': {
                py: 0.5,
              },
            }}
          >
            {opcionesTemporada.map((opcion) => (
              <MenuItem
                key={opcion.value}
                value={opcion.value}
                sx={{
                  fontSize: '0.85rem',
                  justifyContent: 'center',
                }}
              >
                {opcion.label}
              </MenuItem>
            ))}
          </SelectApp>
        </Box>

        {/* Lado derecho: Selector fusionado de Táctica */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end' }}>
          <SelectApp
            value={formacionSeleccionada}
            onChange={(e) => handleFormacionChange(e.target.value)}
            variant="standard"
            disableUnderline
            sx={{
              backgroundColor: 'transparent',
              minWidth: 80,
              fontSize: '0.9rem',
              borderColor: theme.palette.primary.main,
              color: theme.palette.text.primary,
              fontWeight: 400,
            }}
          >
            <ListSubheader
              sx={{
                lineHeight: '32px',
                backgroundColor: theme.palette.background.default,
                fontWeight: 'bold',
                fontSize: '0.65rem',
                color: theme.palette.primary.main,
              }}
            >
              FÚTBOL 11
            </ListSubheader>
            {Object.keys(FORMACIONES.F11).map((f) => (
              <MenuItem key={f} value={f} sx={{ fontSize: '0.85rem', pl: 3 }}>
                {f}
              </MenuItem>
            ))}
            <ListSubheader
              sx={{
                lineHeight: '32px',
                backgroundColor: theme.palette.background.default,
                fontWeight: 'bold',
                fontSize: '0.65rem',
                color: theme.palette.primary.main,
              }}
            >
              FÚTBOL 7
            </ListSubheader>
            {Object.keys(FORMACIONES.F7).map((f) => (
              <MenuItem key={f} value={f} sx={{ fontSize: '0.85rem', pl: 3 }}>
                {f}
              </MenuItem>
            ))}
          </SelectApp>
        </Box>
      </Box>

      {/* Contenido principal */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 2 }}>
        {/* Sidebar */}
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            maxHeight: '74vh',
            overflowY: 'auto',
            pr: 0.5, // Scroll padding
          }}
        >
          {/* Buscador integrado en el sidebar superior (Botón con Dialog) */}
          <Box sx={{ width: '100%', flexShrink: 0 }}>
            <ButtonApp
              contrast
              onClick={() => setOpenDialogAnadir(true)}
              sx={{ width: '100%', py: 1, fontWeight: 'bold' }}
            >
              + Añadir jugadores
            </ButtonApp>
            <DialogApp open={openDialogAnadir} onClose={() => setOpenDialogAnadir(false)} title="Añadir jugadores">
              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Búsqueda global
                  </Typography>
                  <Box sx={{ width: '100%' }}>
                    <BarraBuscarJugador
                      onSelectJugador={(jug) => {
                        handleExtraJugador(jug);
                        setOpenDialogAnadir(false);
                      }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </Box>

                {/* Linea horizontal divisora */}
                <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }} />

                {equiposClub && equiposClub.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      mb: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}
                    >
                      Equipos del club
                    </Typography>
                    <SelectWrapperApp
                      sx={{ width: '50%', alignItems: 'center', justifyContent: 'center' }}
                      value={equipoModalId || ''}
                      onChange={(e) => setEquipoModalId(e.target.value)}
                      options={ordenarPorCategoria(equiposClub)}
                      optionValue="idEquipo"
                      renderLabel={(equipo) => formatClubTeamName(equipo.nombreEquipo, equipo.categoria)}
                    />
                  </Box>
                )}

                {plantillaInferior.length > 0 && (
                  <>
                    <Box
                      sx={{
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        mt: 1,
                      }}
                    >
                      {plantillaInferior.map((jugInf) => {
                        const yaPlanificado = jugadoresPlan.some((j) => j.idJugador === jugInf.idJugador);

                        const codigoCatModal = jugInf.ano ? obtenerCategoriaCodigo(temporadaPlan + 1, jugInf.ano) : '';
                        const estadoCatModal =
                          codigoCatModal && datosEquipo?.categoria
                            ? obtenerEstadoJugadorEnCategoria(codigoCatModal, datosEquipo.categoria)
                            : 0;

                        return (
                          <ListItem
                            key={jugInf.idJugador}
                            sx={{ py: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <PersonAddIcon fontSize="small" color={yaPlanificado ? 'disabled' : 'primary'} />
                            </ListItemIcon>

                            {codigoCatModal && (
                              <BadgeCategoria estadoJugador={estadoCatModal}>{codigoCatModal}</BadgeCategoria>
                            )}

                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">{formatearNombre(jugInf.nombreJugador)}</Typography>
                                  {jugInf.ano && (
                                    <Typography variant="caption" color="text.secondary">
                                      {jugInf.ano}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              primaryTypographyProps={{ color: yaPlanificado ? 'text.secondary' : 'text.primary' }}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                disabled={yaPlanificado}
                                onClick={() => {
                                  handleExtraJugador(
                                    {
                                      idJugador: jugInf.idJugador,
                                      nombreJugador: jugInf.nombreJugador,
                                      ano: jugInf.ano,
                                    },
                                    'filial'
                                  );
                                }}
                                color="primary"
                              >
                                <AddIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            </DialogApp>
          </Box>

          {(() => {
            const jugadoresOrdenados = jugadoresPlan
              .filter((j) => {
                if (j.rol === 'Omitido') {
                  return false;
                }
                const estaEnCampo = j.posicion && j.rol !== 'Espera';
                const anoNac = j.anoNacimiento || j.ano;
                if (!estaEnCampo && !j.esFichaje && datosEquipo?.categoria && anoNac) {
                  return jugadorAptoParaCategoria(temporadaPlan + 1, anoNac, datosEquipo.categoria);
                }
                return true;
              })
              .sort((a, b) => {
                const ordenPosicionesDetallado = {
                  Portero: 1,
                  // Defensas
                  'Defensa central': 10,
                  'Central centro': 11,
                  'Central izquierdo': 12,
                  'Central derecho': 13,
                  'Defensa derecho': 14,
                  'Lateral derecho': 15,
                  'Carrilero derecho': 16,
                  'Defensa izquierdo': 17,
                  'Lateral izquierdo': 18,
                  'Carrilero izquierdo': 19,
                  // Medios
                  Pivote: 30,
                  'Mediocampista central': 31,
                  'Mediocampista izquierdo': 32,
                  'Volante izquierdo': 33,
                  'Interior izquierdo': 34,
                  'Mediocampista derecho': 35,
                  'Volante derecho': 36,
                  'Interior derecho': 37,
                  Mediapunta: 38,
                  // Delanteros
                  Delantero: 50,
                  'Extremo izquierdo': 51,
                  'Extremo derecho': 52,
                  'Delantero izquierdo': 53,
                  'Delantero derecho': 54,
                  'Delantero centro': 55,
                };

                const posA = a.posicion || '';
                const posB = b.posicion || '';

                // Si ambos tienen posición en el campo, usamos el orden detallado
                if (posA && posB) {
                  const ordA = ordenPosicionesDetallado[posA] || 999;
                  const ordB = ordenPosicionesDetallado[posB] || 999;
                  if (ordA !== ordB) {
                    return ordA - ordB;
                  }
                }

                // Si no, recurrimos al grupo general (grupoSidebar)
                const ordenGrupos = {
                  Porteros: 1,
                  Defensas: 2,
                  Centrocampistas: 3,
                  Delanteros: 4,
                  Otros: 5,
                };
                const grA = ordenGrupos[a.grupoSidebar] || 99;
                const grB = ordenGrupos[b.grupoSidebar] || 99;
                if (grA !== grB) {
                  return grA - grB;
                }
                const nombreA = a.nombreJugador || a.nombre || '';
                const nombreB = b.nombreJugador || b.nombre || '';
                return nombreA.localeCompare(nombreB);
              });

            const getGrupoGeneral = (jugador) => {
              if (jugador.grupoSidebar === 'Porteros') {
                return 'Porteros';
              }
              if (jugador.grupoSidebar === 'Defensas') {
                return 'Defensas';
              }
              if (jugador.grupoSidebar === 'Centrocampistas') {
                return 'Centrocampistas';
              }
              if (jugador.grupoSidebar === 'Delanteros') {
                return 'Delanteros';
              }
              return 'Otros';
            };

            return (
              <Box
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropSidebar(e, 'Otros')}
                sx={{ border: `0.5px solid ${theme.palette.divider}`, borderRadius: theme.borderRadius }}
              >
                <BoxTitleApp
                  conFondo
                  sx={{
                    fontSize: theme.typography.textSmall.fontSize,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Jugadores ({jugadoresOrdenados.length})
                </BoxTitleApp>
                <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.4, minHeight: 36 }}>
                  {jugadoresOrdenados.map((j, index) => {
                    const grupoActual = getGrupoGeneral(j);
                    const proximoJugador = jugadoresOrdenados[index + 1];
                    const proximoGrupo = proximoJugador ? getGrupoGeneral(proximoJugador) : null;
                    const mostrarSeparador = proximoGrupo && grupoActual !== proximoGrupo;

                    return (
                      <React.Fragment key={j.idJugador}>
                        {renderFicha(j)}
                        {mostrarSeparador && (
                          <Box
                            sx={{
                              my: 0.8,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              opacity: 0.6,
                              mx: 1,
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                  {jugadoresOrdenados.length === 0 && (
                    <TypographyApp
                      sx={{
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                        textAlign: 'center',
                        py: 1,
                        border: `1px dashed ${theme.palette.divider}`,
                        borderRadius: theme.borderRadius,
                      }}
                    >
                      Arrastra jugadores aquí o al campograma
                    </TypographyApp>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Box>

        {/* Campograma + botones */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <CampogramPlan>
            {formacionActual.posiciones.map((pos) => (
              <Box
                key={pos.id}
                sx={{
                  position: 'absolute',
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                  zIndex: 10,
                }}
              >
                {ROLES.map((rol) => {
                  const jugadorAsignado = jugadoresPlan.find((j) => j.posicion === pos.id && j.rol === rol);
                  return (
                    <SlotRolPlan
                      key={rol}
                      ocupado={!!jugadorAsignado}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDropCampo(e, pos.id, rol)}
                    >
                      {jugadorAsignado && renderFicha(jugadorAsignado, true)}
                    </SlotRolPlan>
                  );
                })}
              </Box>
            ))}
          </CampogramPlan>
        </Box>
      </Box>
    </PaperApp>
  );
};

export default PlanificadorPlantilla;
